'use client';

import React, { useState, useEffect, useRef } from 'react';

/* ─── Aperture mark ─────────────────────────────────────────────────────── */
const ApertureMark = ({ size = 16, color = '#1F4D3F' }: { size?: number; color?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" width={size} height={size} aria-hidden="true">
    {[0, 60, 120, 180, 240, 300].map((deg) => (
      <g key={deg} transform={`rotate(${deg} 12 12)`}>
        <path d="M 12 5 Q 13.2 9 12 11" />
      </g>
    ))}
  </svg>
);

/* ─── Scenario data ──────────────────────────────────────────────────────── */
interface KPI {
  label: string;
  value: string;
  numericValue?: number;
  prefix?: string;
  suffix?: string;
  isText?: boolean;
}

interface Scenario {
  id: string;
  pill: string;
  tag: string;
  question: string;
  vinaResponse: string;
  kpis: KPI[];
  recommendation: string;
  opportunityLabel: string;
  opportunityValue: string;
  isTextOpportunity?: boolean;
}

const SCENARIOS: Scenario[] = [
  {
    id: 'revenue',
    pill: 'Revenue',
    tag: '01',
    question: 'Which product categories are creating the most growth opportunities?',
    vinaResponse:
      'Snacks and Dairy continue to outperform across key locations. Strong repeat purchases and increasing basket values indicate sustained demand momentum.',
    kpis: [
      { label: 'Revenue Growth', value: '+18%', numericValue: 18, prefix: '+', suffix: '%' },
      { label: 'Repeat Customers', value: '+12%', numericValue: 12, prefix: '+', suffix: '%' },
      { label: 'Average Basket', value: '+9%', numericValue: 9, prefix: '+', suffix: '%' },
    ],
    recommendation: 'Increase inventory depth across high-performing categories before festive demand increases.',
    opportunityLabel: 'Potential Revenue Opportunity',
    opportunityValue: '₹4.2L',
  },
  {
    id: 'inventory',
    pill: 'Inventory',
    tag: '02',
    question: 'Which products should I prioritize before upcoming demand increases?',
    vinaResponse:
      'Sales velocity and customer demand indicate strong momentum across seven high-performing SKUs.',
    kpis: [
      { label: 'High-Growth SKUs', value: '7', numericValue: 7, isText: true },
      { label: 'Projected Demand', value: '+21%', numericValue: 21, prefix: '+', suffix: '%' },
      { label: 'Revenue Potential', value: '₹3.2L', isText: true },
    ],
    recommendation: 'Increase purchase quantities for high-demand products before demand peaks.',
    opportunityLabel: 'Additional Revenue Potential',
    opportunityValue: '₹3.2L',
  },
  {
    id: 'customers',
    pill: 'Customers',
    tag: '03',
    question: 'Which customers show the highest growth potential?',
    vinaResponse:
      'Several key accounts continue increasing purchase frequency and basket size.',
    kpis: [
      { label: 'Growth Accounts', value: '12', numericValue: 12, isText: true },
      { label: 'Repeat Purchase Growth', value: '+17%', numericValue: 17, prefix: '+', suffix: '%' },
      { label: 'Revenue Opportunity', value: '₹5.8L', isText: true },
    ],
    recommendation: 'Launch targeted loyalty campaigns and category expansion offers.',
    opportunityLabel: 'Customer Growth Opportunity',
    opportunityValue: '₹5.8L',
  },
  {
    id: 'stores',
    pill: 'Stores',
    tag: '04',
    question: 'Which outlet is best positioned for expansion?',
    vinaResponse:
      'Surat continues leading in customer acquisition, category performance, and inventory turnover.',
    kpis: [
      { label: 'Growth Rate', value: '+22%', numericValue: 22, prefix: '+', suffix: '%' },
      { label: 'Inventory Turnover', value: '+16%', numericValue: 16, prefix: '+', suffix: '%' },
      { label: 'Customer Growth', value: '+14%', numericValue: 14, prefix: '+', suffix: '%' },
    ],
    recommendation: 'Replicate successful product mix and promotional strategies across nearby markets.',
    opportunityLabel: 'Expansion Opportunity Identified',
    opportunityValue: 'High Potential Market',
    isTextOpportunity: true,
  },
  {
    id: 'purchasing',
    pill: 'Purchasing',
    tag: '05',
    question: 'What should I purchase now to maximize future sales?',
    vinaResponse:
      'Upcoming demand patterns indicate strong growth opportunities across several fast-moving products.',
    kpis: [
      { label: 'Growth Products', value: '7', numericValue: 7, isText: true },
      { label: 'Demand Window', value: '14 Days', isText: true },
      { label: 'Revenue Potential', value: '₹3.2L', isText: true },
    ],
    recommendation: 'Create purchase orders early to capture upcoming demand.',
    opportunityLabel: 'Demand Opportunity',
    opportunityValue: '₹3.2L',
  },
  {
    id: 'cashflow',
    pill: 'Cash Flow',
    tag: '06',
    question: 'How can I improve cash availability over the next 30 days?',
    vinaResponse:
      'Current cash position remains strong with opportunities to optimize purchasing cycles and collections.',
    kpis: [
      { label: 'Available Cash', value: '₹2.18 Cr', isText: true },
      { label: 'Collection Growth', value: '+14%', numericValue: 14, prefix: '+', suffix: '%' },
      { label: 'Liquidity Opportunity', value: '₹31L', isText: true },
    ],
    recommendation: 'Align procurement timing with projected inflows for stronger cash efficiency.',
    opportunityLabel: 'Working Capital Opportunity',
    opportunityValue: '₹31L',
  },
  {
    id: 'language',
    pill: 'Language AI',
    tag: '07',
    question: 'Bhai konsa product sabse fast grow kar raha hai?',
    vinaResponse:
      'Snacks, Dairy, and Britannia Bourbon continue showing the strongest growth momentum across stores.',
    kpis: [
      { label: 'Top Growth Products', value: '3', numericValue: 3, isText: true },
      { label: 'Category Growth', value: '+18%', numericValue: 18, prefix: '+', suffix: '%' },
      { label: 'Revenue Potential', value: '₹4.2L', isText: true },
    ],
    recommendation: 'Increase inventory before demand accelerates further.',
    opportunityLabel: 'Intent Understood',
    opportunityValue: 'Growth Opportunity Identified',
    isTextOpportunity: true,
  },
];

/* ─── Language demo entries ──────────────────────────────────────────────── */
const LANGUAGE_DEMOS = [
  { lang: 'English', flag: 'EN', query: 'Which category is growing fastest?' },
  { lang: 'Hinglish', flag: 'HI', query: 'Bhai konsa category sabse fast grow kar rahi hai?' },
  { lang: 'Gujarati', flag: 'GU', query: 'Kai category ni demand sauthi vadhare vadhi rahi chhe?' },
  { lang: 'Hindi', flag: 'HI', query: 'Kaunsi category sabse tezi se grow kar rahi hai?' },
];

/* ─── KPI count-up hook ──────────────────────────────────────────────────── */
function useCountUp(target: number, duration = 1200, active = true) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) { setValue(0); return; }
    let start: number | null = null;
    const step = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);
  return value;
}

/* ─── Animated KPI card ──────────────────────────────────────────────────── */
function KPICard({ kpi, index, active }: { kpi: KPI; index: number; active: boolean }) {
  const count = useCountUp(kpi.numericValue ?? 0, 1000 + index * 150, active && !kpi.isText);
  const display = kpi.isText ? kpi.value : `${kpi.prefix ?? ''}${count}${kpi.suffix ?? ''}`;

  return (
    <div
      className="px-4 py-4 rounded-2xl flex flex-col gap-1"
      style={{
        background: 'rgba(31,77,63,0.04)',
        border: '1px solid rgba(31,77,63,0.09)',
        opacity: active ? 1 : 0,
        transform: active ? 'translateY(0)' : 'translateY(8px)',
        transition: `opacity 0.4s ease ${index * 0.1}s, transform 0.4s ease ${index * 0.1}s`,
      }}
    >
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#7B8F88', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {kpi.label}
      </p>
      <p style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: '1.2rem', color: '#0D2620', letterSpacing: '-0.03em', lineHeight: 1 }}>
        {display}
      </p>
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */
export default function LiveProductExperience() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [langIdx, setLangIdx] = useState(0);
  const prevIdx = useRef(0);
  const scenario = SCENARIOS[activeIdx];

  /* Trigger reveal animation on scenario change */
  useEffect(() => {
    setRevealed(false);
    const t = setTimeout(() => setRevealed(true), 80);
    return () => clearTimeout(t);
  }, [activeIdx]);

  /* Rotate language demo every 2.4s when Language AI is active */
  useEffect(() => {
    if (scenario.id !== 'language') return;
    const interval = setInterval(() => setLangIdx((i) => (i + 1) % LANGUAGE_DEMOS.length), 2400);
    return () => clearInterval(interval);
  }, [scenario.id]);

  const handleScenarioChange = (idx: number) => {
    prevIdx.current = activeIdx;
    setActiveIdx(idx);
  };

  return (
    <section
      id="live-product"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #E6F1FB 0%, #F6F5F0 55%, #FBFAF6 100%)' }}
      aria-label="Live Business Intelligence"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[400px] opacity-20"
          style={{ background: 'radial-gradient(ellipse, rgba(31,77,63,0.12) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] opacity-10"
          style={{ background: 'radial-gradient(ellipse, rgba(79,155,122,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ── */}
        <div className="text-center mb-14">
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.1em', color: '#7B8F88', textTransform: 'uppercase', marginBottom: '14px' }}>
            Live Business Intelligence
          </p>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.035em', color: '#0D2620', lineHeight: 1.08, marginBottom: '16px' }}>
            See VINA in action.
          </h2>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: '1.1rem', color: '#3D5550', maxWidth: '500px', margin: '0 auto', lineHeight: 1.65 }}>
            7 real business scenarios. Growth-focused recommendations. Smarter decisions.
          </p>
        </div>

        {/* ── Scenario pills ── */}
        <div className="flex flex-wrap justify-center gap-2.5 mb-4">
          {SCENARIOS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => handleScenarioChange(i)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{
                background: activeIdx === i ? '#1F4D3F' : 'rgba(255,255,255,0.78)',
                color: activeIdx === i ? '#FFFFFF' : '#4F665F',
                border: activeIdx === i ? '1px solid #1F4D3F' : '1px solid #DDE7E3',
                boxShadow: activeIdx === i ? '0 4px 18px rgba(31,77,63,0.22)' : '0 1px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.28s cubic-bezier(0.22,1,0.36,1)',
                backdropFilter: 'blur(8px)',
              }}
              aria-pressed={activeIdx === i}
            >
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', opacity: 0.65 }}>{s.tag}</span>
              {s.pill}
            </button>
          ))}
        </div>
        <p className="text-center mb-10" style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#9AADA7', letterSpacing: '0.04em' }}>
          7 real business scenarios powered by operational intelligence.
        </p>

        {/* ── Workspace ── */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(28px)',
            border: '1px solid #DDE7E3',
            boxShadow: '0 32px 80px rgba(31,77,63,0.10), 0 8px 24px rgba(118,147,160,0.08)',
          }}
        >
          {/* Window chrome */}
          <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #DDE7E3' }}>
            <div className="flex items-center gap-2.5">
              <ApertureMark size={15} color="#1F4D3F" />
              <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: '13px', color: '#16352D', letterSpacing: '-0.02em' }}>vina</span>
              <span className="px-2 py-0.5 rounded-full" style={{ background: 'rgba(31,77,63,0.08)', fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#1F4D3F', letterSpacing: '0.05em' }}>
                {scenario.tag} · {scenario.pill}
              </span>
            </div>
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#F5C6C6' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#F5E6C6' }} />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#C6E8D4' }} />
            </div>
          </div>

          <div className="p-6 flex flex-col gap-5">

            {/* ── Question bubble ── */}
            <div
              className="flex justify-end"
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity 0.35s ease 0.05s, transform 0.35s ease 0.05s',
              }}
            >
              <div
                className="max-w-[78%] px-4 py-3 rounded-2xl rounded-br-md text-sm"
                style={{ background: 'rgba(31,77,63,0.08)', color: '#16352D', fontFamily: 'var(--font-sans)', lineHeight: 1.55 }}
              >
                {scenario.question}
              </div>
            </div>

            {/* ── VINA response ── */}
            <div
              className="flex items-start gap-3"
              style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease 0.15s, transform 0.4s ease 0.15s',
              }}
            >
              <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
                style={{ background: 'rgba(31,77,63,0.08)', border: '1px solid rgba(31,77,63,0.12)' }}>
                <ApertureMark size={13} color="#1F4D3F" />
              </div>
              <div className="flex-1">
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: '#2A4A42', lineHeight: 1.65 }}>
                  {scenario.vinaResponse}
                </p>
              </div>
            </div>

            {/* ── KPI cards ── */}
            <div className="grid grid-cols-3 gap-3">
              {scenario.kpis.map((kpi, i) => (
                <KPICard key={`${scenario.id}-${i}`} kpi={kpi} index={i} active={revealed} />
              ))}
            </div>

            {/* ── Language AI demo (only for scenario 07) ── */}
            {scenario.id === 'language' && (
              <div
                className="rounded-2xl p-4"
                style={{
                  background: 'rgba(31,77,63,0.03)',
                  border: '1px solid rgba(31,77,63,0.10)',
                  opacity: revealed ? 1 : 0,
                  transition: 'opacity 0.4s ease 0.35s',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#4F9B7A' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#4F9B7A', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Understands intent. Answers naturally.
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  {LANGUAGE_DEMOS.map((demo, i) => (
                    <div
                      key={demo.lang}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl"
                      style={{
                        background: i === langIdx ? 'rgba(31,77,63,0.07)' : 'transparent',
                        border: i === langIdx ? '1px solid rgba(31,77,63,0.12)' : '1px solid transparent',
                        transition: 'all 0.4s ease',
                      }}
                    >
                      <span className="px-1.5 py-0.5 rounded text-xs font-mono"
                        style={{ background: 'rgba(31,77,63,0.08)', color: '#1F4D3F', fontSize: '9px', letterSpacing: '0.04em', minWidth: '24px', textAlign: 'center' }}>
                        {demo.lang.slice(0, 2).toUpperCase()}
                      </span>
                      <span style={{ fontFamily: 'var(--font-sans)', fontSize: '12px', color: i === langIdx ? '#0D2620' : '#7B8F88', transition: 'color 0.4s ease' }}>
                        {demo.query}
                      </span>
                      {i === langIdx && (
                        <span className="ml-auto" style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#4F9B7A', letterSpacing: '0.04em' }}>
                          ↓ same insight
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Recommendation ── */}
            <div
              className="flex items-start gap-3 px-4 py-3.5 rounded-2xl"
              style={{
                background: 'rgba(31,77,63,0.05)',
                border: '1px solid rgba(31,77,63,0.10)',
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease 0.3s, transform 0.4s ease 0.3s',
              }}
            >
              <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>💡</span>
              <div>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: '#4F9B7A', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Business Recommendation
                </p>
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: '#1A3530', lineHeight: 1.6 }}>
                  {scenario.recommendation}
                </p>
              </div>
            </div>

            {/* ── Opportunity card ── */}
            <div
              className="rounded-2xl px-5 py-4 flex items-center justify-between"
              style={{
                background: 'linear-gradient(135deg, rgba(31,77,63,0.07) 0%, rgba(79,155,122,0.06) 100%)',
                border: '1px solid rgba(79,155,122,0.22)',
                boxShadow: revealed ? '0 0 28px rgba(79,155,122,0.14), 0 4px 16px rgba(31,77,63,0.08)' : 'none',
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.98)',
                transition: 'opacity 0.45s ease 0.42s, transform 0.45s ease 0.42s, box-shadow 0.45s ease 0.42s',
              }}
            >
              <div>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#4F9B7A', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '4px' }}>
                  {scenario.opportunityLabel}
                </p>
                <p style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: scenario.isTextOpportunity ? '1rem' : '1.6rem', color: '#1F4D3F', letterSpacing: '-0.03em', lineHeight: 1 }}>
                  {scenario.opportunityValue}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(79,155,122,0.15)', border: '1px solid rgba(79,155,122,0.25)' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 2L8 14M3 7L8 2L13 7" stroke="#4F9B7A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

          </div>
        </div>

        {/* ── Footer tagline ── */}
        <p className="text-center mt-10" style={{ fontFamily: 'var(--font-sans)', fontSize: '0.95rem', color: '#5A7268', lineHeight: 1.65, maxWidth: '520px', margin: '40px auto 0' }}>
          VINA helps businesses uncover opportunities, make smarter decisions, and accelerate growth through intelligent business conversations.
        </p>

      </div>

      {/* ── Keyframe styles ── */}
      <style>{`
        @keyframes vinaWorkspaceBreathe {
          0%, 100% { box-shadow: 0 32px 80px rgba(31,77,63,0.10), 0 8px 24px rgba(118,147,160,0.08); }
          50% { box-shadow: 0 36px 90px rgba(31,77,63,0.14), 0 10px 28px rgba(118,147,160,0.10); }
        }
      `}</style>
    </section>
  );
}
